/**
 * Services API
 *
 * API functions for managing contractor services
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';

export interface Service {
  id: string;
  contractorId: string;
  categoryId: string; // Subcategory ID (required)
  name: string;
  description: string | null;
  price: number | null;
  unit: string | null; // e.g., "per hour", "per project"
  createdAt: string;
  updatedAt: string;
  category?: {
    id: string;
    nameEn: string;
    nameFr: string;
  };
}

export interface CreateServiceDto {
  categoryId: string; // Required - must be associated with a category/subcategory
  name: string;
  description?: string;
  price?: number;
  unit?: string;
}

export interface UpdateServiceDto {
  categoryId?: string;
  name?: string;
  description?: string;
  price?: number;
  unit?: string;
}

/**
 * Get contractor's services
 */
export async function getMyServices(): Promise<Service[]> {
  const url = buildApiUrl('/contractors/me/services');

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    credentials: 'include',
  });

  return handleApiResponse<Service[]>(response);
}

/**
 * Create a new service
 */
export async function createService(dto: CreateServiceDto): Promise<Service> {
  const url = buildApiUrl('/contractors/me/services');

  const response = await fetchWithTokenRefresh(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(dto),
  });

  return handleApiResponse<Service>(response);
}

/**
 * Update a service
 */
export async function updateService(
  serviceId: string,
  dto: UpdateServiceDto
): Promise<Service> {
  const url = buildApiUrl(`/contractors/me/services/${serviceId}`);

  const response = await fetchWithTokenRefresh(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(dto),
  });

  return handleApiResponse<Service>(response);
}

/**
 * Delete a service
 */
export async function deleteService(serviceId: string): Promise<void> {
  const url = buildApiUrl(`/contractors/me/services/${serviceId}`);

  const response = await fetchWithTokenRefresh(url, {
    method: 'DELETE',
    credentials: 'include',
  });

  await handleApiResponse<void>(response);
}
